import React, { useState, useEffect, useCallback } from 'react';
import { Gift, UploadCloud, X, Award } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export const CampaignBoard = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [approvedCount, setApprovedCount] = useState(0);

  // Form State adapted for dynamic Level / Semester contexts
  const [formData, setFormData] = useState({
    school: '',
    department: '',
    courseTitle: '',
    courseCode: '',
    institutionType: 'university', 
    level: '',
    semester: '1',
    year: new Date().getFullYear() - 1,
  });
  
  // Changed from null to empty array to track multi-page images
  const [files, setFiles] = useState([]);

  // Clear level choice when switching between Uni and Poly
  useEffect(() => {
    setFormData(prev => ({ ...prev, level: '' }));
  }, [formData.institutionType]);

  const fetchContributionProgress = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('contributions')
        .select('course_code, year')
        .eq('user_id', user.id)
        .eq('status', 'approved');

      if (error) throw error;
      
      const uniqueCombos = new Set(data.map(item => `${item.course_code.trim().toUpperCase()}_${item.year}`));
      setApprovedCount(uniqueCombos.size);
    } catch (err) {
      console.error('Error fetching contribution stats:', err);
    }
  }, [user]); 

  useEffect(() => {
    fetchContributionProgress();
  }, [fetchContributionProgress]); 

  if (!isOpen) return null;
  const currentMilestoneProgress = approvedCount % 5;

  const handleUploadContribution = async (e) => {
    e.preventDefault();
    if (files.length === 0) return toast.error('Please attach at least one page or exam image');
    if (!formData.level) return toast.error('Please select your level/class tier');

    setIsUploading(true);
    try {
      const uploadedFilePaths = [];

      // Loop through and upload all multi-page files selected by the user
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileExt = file.name.split('.').pop();
        
        // Organized path: past_questions/user_id/timestamp_page.ext
        const fileName = `${user.id}/${Date.now()}_page${i}.${fileExt}`;
        const filePath = `${fileName}`; 

        // 1. Point .from() to your storage bucket 🎯
        const { error: uploadError } = await supabase.storage
          .from('past_questions') 
          .upload(filePath, file);

        // Catch storage errors early so it doesn't try to insert empty rows into DB
        if (uploadError) {
          console.error("Storage upload error details:", uploadError);
          throw new Error(`File upload failed: ${uploadError.message}`);
        }
        
        // 2. Get public URL path from the correct bucket
        const { data: publicUrlData } = supabase.storage
          .from('past_questions')
          .getPublicUrl(filePath);

        uploadedFilePaths.push(publicUrlData.publicUrl);
      }

      // 3. Write data safely to the database table
      const { error: dbError } = await supabase.from('contributions').insert({
        user_id: user.id,
        school: formData.school.trim(),
        department: formData.department.trim(),
        course_title: formData.courseTitle.trim(),
        course_code: formData.courseCode.trim().toUpperCase(),
        institution_type: formData.institutionType,
        level: formData.level,
        semester: formData.semester,
        year: parseInt(formData.year),
        file_url: uploadedFilePaths, // Saved as Postgres Array string values
        status: 'pending'
      });

      if (dbError) throw dbError;

      toast.success('Submitted for review! Duplicate papers or duplicate years won\'t count.');
      setShowForm(false);
      setFormData({ 
        school: '', 
        department: '', 
        courseCode: '', 
        institutionType: 'university', 
        level: '', 
        semester: '1', 
        year: new Date().getFullYear() - 1 
      });
      setFiles([]);
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Submission failed');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="relative overflow-hidden mb-6 rounded-2xl bg-gradient-to-r from-brown-700 via-brown-800 to-brown-900 p-5 sm:p-6 text-white shadow-md border border-brown-900/50">
      <button 
        onClick={() => setIsOpen(false)}
        className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
      >
        <X className="w-5 h-5" />
      </button>

      {!showForm ? (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start space-x-4 max-w-2xl">
            <div className="p-3 bg-white/10 backdrop-blur-md rounded-xl shrink-0 border border-white/10 hidden sm:block">
              <Gift className="w-6 h-6 text-yellow-400 animate-pulse" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2">
                Earn 1 Week Free Premium! <span className="text-yellow-400">🎁</span>
              </h2>
              <p className="text-xs sm:text-sm text-cream-100/90 mt-1 leading-relaxed">
                Upload 5 unique past exam papers from your department (Universities or Polytechnics). Once verified, we will instantly credit your account with 1 week of premium access. 
              </p>
              
              {/* Progress Indicator Bar */}
              <div className="mt-4 flex items-center space-x-3 bg-black/20 p-2.5 rounded-xl border border-white/5 max-w-md">
                <Award className="w-4 h-4 text-yellow-400 shrink-0" />
                <div className="flex-1">
                  <div className="flex justify-between text-[11px] font-semibold mb-1">
                    <span>Milestone Progress</span>
                    <span>{currentMilestoneProgress} / 5 Approved</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-yellow-400 to-amber-400 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${(currentMilestoneProgress / 5) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="shrink-0 flex items-center justify-center space-x-2 px-5 py-2.5 bg-white text-brown-900 font-semibold text-sm rounded-xl hover:bg-cream-50 transition-all shadow-sm self-start md:self-center"
          >
            <UploadCloud className="w-4 h-4 text-brown-700" />
            <span>Contribute Now</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleUploadContribution} className="space-y-4 max-w-3xl">
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <h3 className="font-semibold text-sm sm:text-base text-yellow-400 flex items-center gap-2">
              <UploadCloud className="w-4 h-4" /> Submit Unique Exam Paper ({currentMilestoneProgress}/5)
            </h3>
            <button 
              type="button" 
              onClick={() => setShowForm(false)}
              className="text-xs text-white/70 hover:text-white underline"
            >
              Cancel
            </button>
          </div>

          {/* Form Rows */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-brown-900">
            <input
              type="text"
              placeholder="School (e.g., YABATECH, UNILAG)"
              required
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-cream-200 rounded-lg focus:outline-none"
              value={formData.school}
              onChange={e => setFormData({...formData, school: e.target.value})}
            />
            <input
              type="text"
              placeholder="Department (e.g., Computer Science)"
              required
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-cream-200 rounded-lg focus:outline-none"
              value={formData.department}
              onChange={e => setFormData({...formData, department: e.target.value})}
            />
            <input
              type="text"
              placeholder="Course Title (e.g., Intro to Electronics)"
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-cream-200 rounded-lg focus:outline-none"
              value={formData.courseTitle}
              onChange={e => setFormData({...formData, courseTitle: e.target.value})}
            />
            <input
              type="text"
              placeholder="Course Code (e.g., COM 111, CSC 201)"
              required
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-cream-200 rounded-lg focus:outline-none"
              value={formData.courseCode}
              onChange={e => setFormData({...formData, courseCode: e.target.value})}
            />
            <input
              type="number"
              placeholder="Exam Year"
              required
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-cream-200 rounded-lg focus:outline-none"
              value={formData.year}
              onChange={e => setFormData({...formData, year: e.target.value})}
            />
          </div>

          {/* New Filter Tiers Row for Institution Context */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-brown-900">
            <select
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-cream-200 rounded-lg focus:outline-none font-medium text-brown-700 cursor-pointer"
              value={formData.institutionType}
              onChange={e => setFormData({...formData, institutionType: e.target.value})}
            >
              <option value="university">University</option>
              <option value="polytechnic">Polytechnic</option>
            </select>

            <select
              required
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-cream-200 rounded-lg focus:outline-none font-medium text-brown-700 cursor-pointer"
              value={formData.level}
              onChange={e => setFormData({...formData, level: e.target.value})}
            >
              <option value="">-- Choose Level --</option>
              {formData.institutionType === 'university' ? (
                <>
                  <option value="100">100 Lvl</option>
                  <option value="200">200 Lvl</option>
                  <option value="300">300 Lvl</option>
                  <option value="400">400 Lvl</option>
                  <option value="500">500 Lvl</option>
                </>
              ) : (
                <>
                  <option value="ND 1">ND 1</option>
                  <option value="ND 2">ND 2</option>
                  <option value="HND 1">HND 1</option>
                  <option value="HND 2">HND 2</option>
                </>
              )}
            </select>

            <select
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-cream-200 rounded-lg focus:outline-none font-medium text-brown-700 cursor-pointer"
              value={formData.semester}
              onChange={e => setFormData({...formData, semester: e.target.value})}
            >
              <option value="1">1st Semester Exam</option>
              <option value="2">2nd Semester Exam</option>
            </select>
          </div>

          {/* File input accepting multiple selections */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-1">
            <input 
              type="file" 
              accept=".pdf,image/*"
              multiple 
              required
              onChange={e => setFiles(Array.from(e.target.files))}
              className="text-xs text-white/80 cursor-pointer file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-white/10 file:text-white"
            />
            <button
              type="submit"
              disabled={isUploading}
              className="px-5 py-2 bg-yellow-500 hover:bg-yellow-400 text-brown-950 text-xs sm:text-sm font-bold rounded-lg transition-all disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : 'Submit Paper'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};